import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, List, ListItem, Typography, useMediaQuery, useTheme } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { TariffCategoryCell } from '../TariffCategoryCell'
import { TariffCategory } from '../../../shared/models/types'
import { TariffsStatusChip } from '../../../shared/components/TariffsStatusChip'
import { getCurrencyValue } from '../../../shared/utils/currency-value'
import { TariffChart } from './tariff-chart/TariffChart'
import { TariffDetailsModel } from '../../models/single-tariff-model'
import { format } from '@e3dc-react/shell/Libs/date'

const useStyles = makeStyles()(theme => ({
  listItem: {
    alignItems: 'center',
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(2.5),
    paddingLeft: 0,
  },
  listItemTitle: {
    overflowWrap: 'anywhere',
    hyphens: 'auto',
  },
  listItemValue: {
    paddingLeft: theme.spacing(1),
    overflowWrap: 'anywhere',
    hyphens: 'auto',
  },
  chartListItem: {
    flexDirection: 'column',
  },
}))

interface TariffDetailsProps {
  tariff: TariffDetailsModel
}

/**
 * Component display details only for static tariffs.
 * @param - single tariff as TariffModel
 */

export const TariffDetails: React.FC<TariffDetailsProps> = ({ tariff }) => {
  const { t } = useCustomTranslation()
  const { classes, css } = useStyles()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const titleWidth = isMobile ? '68%' : '43%'
  const valueWidth = isMobile ? '32%' : '57%'

  const isTariffDynamic = tariff.tariffCategory === TariffCategory.dynamicTariff

  return (
    <List>
      <ListItem divider className={classes.listItem} disableGutters>
        <Typography variant="subtitle1" className={css(classes.listItemTitle, { width: titleWidth })}>
          {t('emobility.tariffs.tariff-category')}
        </Typography>
        <TariffCategoryCell category={tariff.tariffCategory} />
      </ListItem>
      <ListItem divider className={classes.listItem} disableGutters>
        <Typography variant="subtitle1" className={css(classes.listItemTitle, { width: titleWidth })}>
          {t('emobility.tariffs.status')}
        </Typography>
        <TariffsStatusChip status={tariff.tariffStatus} />
      </ListItem>

      {isTariffDynamic ? (
        <>
          <ListItem disableGutters className={classes.chartListItem}>
            <Typography variant="subtitle1" width={'100%'}>
              {t('emobility.tariffs.price-details')}
            </Typography>
          </ListItem>

          <Box my={3}>
            <TariffChart tariff={tariff} />
          </Box>
        </>
      ) : (
        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={css(classes.listItemTitle, { width: titleWidth })}>
            {t('emobility.tariffs.price-details')}
          </Typography>
          <Typography variant="body2" className={css(classes.listItemValue, { width: valueWidth })}>
            {getCurrencyValue(tariff.staticGridPrice, tariff.currencyCode)}
          </Typography>
        </ListItem>
      )}

      <ListItem divider className={classes.listItem} disableGutters>
        <Typography variant="subtitle1" className={css(classes.listItemTitle, { width: titleWidth })}>
          {t('emobility.tariffs.price-solar-details')}
        </Typography>
        <Typography variant="body2" className={css(classes.listItemValue, { width: valueWidth })}>
          {getCurrencyValue(tariff.solarPrice, tariff.currencyCode)}
        </Typography>
      </ListItem>
      <ListItem divider className={classes.listItem} disableGutters>
        <Typography variant="subtitle1" className={css(classes.listItemTitle, { width: titleWidth })}>
          {t('emobility.tariffs.discharge-price-details')}
        </Typography>
        <Typography variant="body2" className={css(classes.listItemValue, { width: valueWidth })}>
          {getCurrencyValue(tariff.dischargePrice, tariff.currencyCode)}
        </Typography>
      </ListItem>

      {!isTariffDynamic && (
        <>
          <ListItem divider className={classes.listItem} disableGutters>
            <Typography variant="subtitle1" className={css(classes.listItemTitle, { width: titleWidth })}>
              {t('emobility.tariffs.startDate')}
            </Typography>
            <Typography variant="body2" className={css(classes.listItemValue, { width: valueWidth })}>
              {tariff.startTime ? format(new Date(tariff.startTime), t('common.format.date-fns-date')) : '-'}
            </Typography>
          </ListItem>
          <ListItem divider className={classes.listItem} disableGutters>
            <Typography variant="subtitle1" className={css(classes.listItemTitle, { width: titleWidth })}>
              {t('emobility.tariffs.endDate')}
            </Typography>
            <Typography variant="body2" className={css(classes.listItemValue, { width: valueWidth })}>
              {tariff.endTime ? format(new Date(tariff.endTime), t('common.format.date-fns-date')) : '-'}
            </Typography>
          </ListItem>
        </>
      )}

      <ListItem className={classes.listItem} disableGutters>
        <Typography variant="subtitle1" className={css(classes.listItemTitle, { width: titleWidth })}>
          {t('emobility.tariffs.last-updated')}
        </Typography>
        <Typography variant="body2" className={css(classes.listItemValue, { width: valueWidth })}>
          {tariff.lastUpdated ? format(new Date(tariff.lastUpdated), t('common.format.date-fns-date')) : '-'}
        </Typography>
      </ListItem>
    </List>
  )
}
